// src/components/music/parts/SearchAndFilter.jsx

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, X, FolderOpen, Grid3X3, List, SlidersHorizontal } from 'lucide-react';
import { MUSIC_TYPES } from '../constants/musicTypes';

export const SearchAndFilter = ({
  searchQuery,
  selectedType,
  selectedAlbum,
  albums,
  groupByAlbum,
  viewMode,
  filteredMusic,
  totalMusic,
  onSearchChange,
  onTypeChange,
  onAlbumChange,
  onGroupChange,
  onViewModeChange
}) => {
  const [showFilters, setShowFilters] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="mt-12 sm:mt-8 mb-4 flex flex-col gap-3 bg-[#121318]/80 backdrop-blur-xl p-3 sm:p-4 rounded-2xl border border-white/10 shadow-xl"
    >
      {/* INPUTS ROW */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center text-white">
        {/* Search Input */}
        <div className="flex-1 relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#bd9f67]" />
          <input
            type="text"
            placeholder="Search tracks..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-9 pr-9 py-2 bg-black/40 text-white placeholder-white/40 border border-white/10 rounded-lg focus:outline-none focus:border-[#bd9f67] focus:ring-1 focus:ring-[#bd9f67] text-sm transition-all shadow-inner"
          />
          {searchQuery && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition"
              title="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Mobile Filter Toggle */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="sm:hidden flex items-center justify-center gap-2 px-4 py-2 bg-black/40 text-white/90 border border-white/10 rounded-lg focus:outline-none focus:border-[#bd9f67] focus:ring-1 focus:ring-[#bd9f67] text-sm transition-all"
        >
          <SlidersHorizontal size={16} />
          {showFilters ? 'Hide Filters' : 'Filters'}
        </button>

        {/* Type Filter Dropdown */}
        <div className={`relative ${showFilters ? 'block' : 'hidden'} sm:block`}>
          <select
            value={selectedType}
            onChange={(e) => onTypeChange(e.target.value)}
            className="w-full sm:w-auto px-3 py-2 bg-black/40 text-white/90 border border-white/10 rounded-lg focus:outline-none focus:border-[#bd9f67] focus:ring-1 focus:ring-[#bd9f67] text-sm transition-all cursor-pointer appearance-none pr-8"
          >
            <option value="all" className="bg-[#181a22] text-white">
              All Types
            </option>
            {MUSIC_TYPES.map(type => (
              <option key={type.value} value={type.value} className="bg-[#181a22] text-white">
                {type.label}
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2.5 text-white/40">
            <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20">
              <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
            </svg>
          </div>
        </div>

        {/* Album Filter Dropdown */}
        <div className={`relative ${showFilters ? 'block' : 'hidden'} sm:block`}>
          <select
            value={selectedAlbum}
            onChange={(e) => onAlbumChange(e.target.value)}
            className="w-full sm:w-auto px-3 py-2 bg-black/40 text-white/90 border border-white/10 rounded-lg focus:outline-none focus:border-[#bd9f67] focus:ring-1 focus:ring-[#bd9f67] text-sm transition-all cursor-pointer appearance-none pr-8"
          >
            <option value="all" className="bg-[#181a22] text-white">
              All Albums
            </option>
            <option value="" className="bg-[#181a22] text-white">
              No Album
            </option>
            {albums.map(album => (
              <option key={album} value={album} className="bg-[#181a22] text-white">
                {album}
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2.5 text-white/40">
            <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20">
              <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
            </svg>
          </div>
        </div>
      </div>

      {/* VIEW & GROUP OPTIONS */}
      <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-white/5">
        {albums.length > 0 ? (
          <label className="text-xs font-medium text-white/80 cursor-pointer flex items-center gap-2 hover:text-[#bd9f67] transition-colors select-none">
            <input
              type="checkbox"
              id="groupByAlbum"
              checked={groupByAlbum}
              onChange={(e) => onGroupChange(e.target.checked)}
              className="w-3.5 h-3.5 accent-[#bd9f67] rounded border-white/20 bg-black/40 focus:ring-0 focus:ring-offset-0"
            />
            <FolderOpen size={14} className="text-[#bd9f67]" />
            Group by Album
          </label>
        ) : (
          <div />
        )}

        {/* View Mode Toggle Button */}
        {!groupByAlbum && (
          <div className="flex items-center gap-1 bg-black/40 p-0.5 rounded-lg border border-white/5">
            <button
              onClick={() => onViewModeChange('card')}
              className={`p-1.5 rounded-md transition-all ${
                viewMode === 'card'
                  ? 'bg-[#bd9f67] text-gray-950 font-semibold shadow-md'
                  : 'text-white/60 hover:text-white'
              }`}
              title="Card View"
            >
              <Grid3X3 size={16} />
            </button>
            <button
              onClick={() => onViewModeChange('table')}
              className={`p-1.5 rounded-md transition-all ${
                viewMode === 'table'
                  ? 'bg-[#bd9f67] text-gray-950 font-semibold shadow-md'
                  : 'text-white/60 hover:text-white'
              }`}
              title="Table View"
            >
              <List size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Results Count Badge */}
      <div className="text-[11px] text-white/50 flex items-center gap-1">
        Showing <span className="font-semibold text-[#bd9f67]">{filteredMusic}</span> of {totalMusic} tracks
        {albums.length > 0 && ` • ${albums.length} album${albums.length !== 1 ? 's' : ''}`}
      </div>
    </motion.div>
  );
};